import { createTheme } from "@mui/material/styles";

const baseTypography = {
  fontFamily: `"Inter", "Roboto", "Helvetica", "Arial", sans-serif`,
  fontSize: 13,
  htmlFontSize: 15,
  h1: { fontWeight: 700, fontSize: "1.9rem" },
  h2: { fontWeight: 700, fontSize: "1.6rem" },
  h3: { fontWeight: 700, fontSize: "1.4rem" },
  h4: { fontWeight: 700, fontSize: "1.2rem" },
  h5: { fontWeight: 700, fontSize: "1.05rem" },
  h6: { fontWeight: 600, fontSize: "0.95rem" },
  subtitle1: { fontSize: "0.9rem" },
  subtitle2: { fontSize: "0.8rem" },
  body1: { fontSize: "0.85rem" },
  body2: { fontSize: "0.8rem" },
  caption: { fontSize: "0.72rem" },
  button: { textTransform: "none", fontWeight: 600, fontSize: "0.8rem" },
};

const componentOverrides = (mode) => ({
  MuiButton: {
    defaultProps: { size: "small" },
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: "none",
        fontWeight: 600,
        paddingTop: 4,
        paddingBottom: 4,
        minHeight: 30,
      },
      sizeSmall: { padding: "3px 10px", fontSize: "0.78rem" },
    },
  },
  MuiIconButton: {
    defaultProps: { size: "small" },
    styleOverrides: {
      sizeSmall: { padding: 5 },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        borderRadius: 10,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        boxShadow:
          mode === "light"
            ? "0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)"
            : "0 1px 3px rgba(0,0,0,0.4)",
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 12,
        "&:last-child": { paddingBottom: 12 },
      },
    },
  },
  MuiTextField: {
    defaultProps: { size: "small", variant: "outlined" },
  },
  MuiSelect: { defaultProps: { size: "small" } },
  MuiInputBase: {
    styleOverrides: {
      root: { fontSize: "0.82rem" },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: "0.82rem" },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { fontSize: "0.7rem", marginTop: 2 },
    },
  },
  MuiTable: {
    defaultProps: { size: "small" },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { fontSize: "0.78rem", padding: "6px 10px" },
      head: {
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: 0.3,
        textTransform: "uppercase",
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: { paddingTop: 5, paddingBottom: 5 },
    },
  },
  MuiChip: {
    defaultProps: { size: "small" },
    styleOverrides: {
      root: { borderRadius: 6, fontWeight: 600 },
      sizeSmall: { height: 20, fontSize: "0.7rem" },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: { fontSize: "0.82rem" },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 38,
        padding: "6px 12px",
        fontSize: "0.8rem",
        textTransform: "none",
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 38 },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { fontSize: "1rem", padding: "12px 16px" },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: { padding: "12px 16px" },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: { padding: "8px 16px" },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { fontSize: "0.8rem", padding: "4px 12px" },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      dense: { minHeight: 44 },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        boxShadow: "none",
        borderBottom:
          mode === "light"
            ? "1px solid rgba(16,24,40,0.08)"
            : "1px solid rgba(255,255,255,0.08)",
      },
    },
  },
});

export const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: "#2563eb", light: "#60a5fa", dark: "#1d4ed8" },
      secondary: { main: "#9333ea" },
      success: { main: "#16a34a" },
      warning: { main: "#f59e0b" },
      error: { main: "#dc2626" },
      info: { main: "#0ea5e9" },
      ...(mode === "light"
        ? {
            background: { default: "#f6f7fb", paper: "#ffffff" },
            text: { primary: "#0f172a", secondary: "#475569" },
          }
        : {
            background: { default: "#0b1020", paper: "#111735" },
            text: { primary: "#f1f5f9", secondary: "#94a3b8" },
          }),
    },
    shape: { borderRadius: 10 },
    spacing: 6,
    typography: baseTypography,
    components: componentOverrides(mode),
  });
